import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { UniBox, type UniBoxViewPatch } from '../UniBox';
import { salesConfig } from '../UniBox/configs';
import { salesApi } from '../UniBox/dummyData';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { backendProvider } from '../../services/backend';
import { createMockSalesApi, createRealSalesApi, mockDataVolumeStore } from '../../services/mock';
import { MockDataSwitch } from '../atoms/MockDataSwitch';
import { SalesDataSourceToggle } from '../atoms/SalesDataSourceToggle';
import { normalizeSellerOptions } from '../modules/CampaignCreator/utils/sellerOptions';
import type { CampaignMetadata, ISRNotesDocument, ISRUpdateDiff } from '../../types';

/**
 * Sales Intelligence tab.
 *
 * For superadmin-labelled users this swaps the dummy `salesApi` for a
 * schema-accurate mock (`createMockSalesApi`) fed by `n8nServiceMock`, and
   * surfaces the `MockDataSwitch` (0 / Some / Huge) in the board toolbar. Everyone
 * else keeps the existing dummy-data path — production behaviour is unchanged.
 *
 * The user email that owns the generated dataset: the impersonation target if
 * an admin is impersonating, else the signed-in user. Mirrors how the real
 * n8n-proxy resolves the caller.
 */
const resolveOwnerEmail = (): string =>
  backendProvider.auth.getTargetEmail() || backendProvider.auth.getCurrentUser()?.email || 'demo@brainiacs.local';

type UniBoxLens = 'pipeline' | 'outreach' | 'inbox' | 'all';

interface SalesUniBoxTabProps {
  campaigns?: CampaignMetadata[];
  sellerIds?: unknown;
  // URL-driven view state (optional → UniBox keeps local defaults when absent).
  lens?: UniBoxLens;
  view?: 'inbox' | 'calendar' | 'kanban';
  mailbox?: 'inbox' | 'queue';
  calRange?: 'month' | 'week';
  onLensChange?: (next: UniBoxLens) => void;
  onViewChange?: (next: 'inbox' | 'calendar' | 'kanban') => void;
  onMailboxChange?: (next: 'inbox' | 'queue') => void;
  onCalRangeChange?: (next: 'month' | 'week') => void;
  /** Batched view-state change (one URL write per action). See UniBoxViewPatch. */
  onUniBoxChange?: (patch: UniBoxViewPatch) => void;
  /** Open a lead's full profile (Leads Intelligence detail). */
  onOpenLead?: (lvl1Id: number, campaignId?: string) => void;
  /** Open a campaign's details surface (e.g. a modal). */
  onOpenCampaignDetails?: (groupId: string) => void;
  /**
   * Persist a sales status/substatus change from the ISR drawer (same
   * update_campaign action the discard flow uses). When omitted the stepper
   * stays read-only — dummy/mock feeds have nothing real to write.
   */
  onSaveSalesState?: (entity: { campaign_id?: number | string; lvl2_id?: number | string; icp_score?: number | null }, sales: { lead_status: string; lead_substatus: string | null }, notes: ISRNotesDocument, updated?: ISRUpdateDiff) => void | Promise<void>;
}

const SalesUniBoxTab: React.FC<SalesUniBoxTabProps> = ({
  campaigns,
  sellerIds,
  lens,
  view,
  mailbox,
  calRange,
  onLensChange,
  onViewChange,
  onMailboxChange,
  onCalRangeChange,
  onUniBoxChange,
  onOpenLead,
  onOpenCampaignDetails,
  onSaveSalesState,
}) => {
  // Team currency drives opportunity-value formatting across the Kanban.
  const { data: teamPrefs } = useTeamPreferences();

  // Superadmins get the schema-accurate mock + the volume switch.
  const isSuperadmin = !!backendProvider.auth.getCurrentUser()?.labels?.includes('superadmin');

  // Re-create the mock api when the volume changes (the store is process-wide;
  // subscribing bumps a counter so useMemo recomputes and UniBox reloads feed).
  const [volumeTick, setVolumeTick] = React.useState(0);
  React.useEffect(() => mockDataVolumeStore.subscribe(() => setVolumeTick((t) => t + 1)), []);

  // Re-create the api when the data-source toggle flips.
  const [sourceTick, setSourceTick] = React.useState(0);
  React.useEffect(() => mockDataVolumeStore.subscribeSource(() => setSourceTick((t) => t + 1)), []);

  const currentSource = mockDataVolumeStore.getSource();
  const isReal = currentSource === 'real';

  // Error state for the real data path. The mock path never errors (it's
  // in-memory), so this only applies to the real api.
  const [feedError, setFeedError] = React.useState<string | null>(null);
  const [feedLoading, setFeedLoading] = React.useState(false);

  const baseApi = useMemo(() => {
    if (!isSuperadmin) return salesApi;
    void volumeTick;
    void sourceTick;

    if (isReal) {
      return createRealSalesApi(resolveOwnerEmail());
    }

    return createMockSalesApi(resolveOwnerEmail());
  }, [isSuperadmin, isReal, volumeTick, sourceTick]);

  const sellerContextByCampaign = useMemo(() => {
    const sellerNameById = new Map(
      normalizeSellerOptions(sellerIds).map((seller) => [seller.value, seller.label]),
    );

    return new Map((campaigns || []).map((campaign) => {
      const sellerId = campaign.seller_lvl1?.trim() || null;
      const sellerName = campaign.seller_name?.trim()
        || (sellerId ? sellerNameById.get(sellerId) : null)
        || null;
      return [campaign.id, { seller_lvl1: sellerId, seller_name: sellerName }];
    }));
  }, [campaigns, sellerIds]);

  const api = useMemo(() => {
    if (sellerContextByCampaign.size === 0) return baseApi;

    return {
      ...baseApi,
      getFeed: async () => {
        const feed = await baseApi.getFeed();
        if (!Array.isArray(feed.campaigns)) return feed;

        return {
          ...feed,
          campaigns: feed.campaigns.map((group) => {
            if (!group || typeof group !== 'object') return group;
            const groupRecord = group as Record<string, unknown>;
            const campaignId = String(groupRecord.id || '');
            const sellerContext = sellerContextByCampaign.get(campaignId);
            return sellerContext ? { ...groupRecord, ...sellerContext } : group;
          }),
        };
      },
    };
  }, [baseApi, sellerContextByCampaign]);

  // Wrap the api to catch errors at the tab level and surface a banner.
  // The toggle stays interactive during the error state so the user can
  // switch back to mock.
  const wrappedApi = useMemo(() => {
    if (!isReal) return api;

    return {
      ...api,
      getFeed: async () => {
        setFeedError(null);
        setFeedLoading(true);
        try {
          const feed = await api.getFeed();
          setFeedLoading(false);
          return feed;
        } catch (err: any) {
          const message = err?.message || 'Failed to load real data';
          setFeedError(message);
          setFeedLoading(false);
          // Return empty feed so the board doesn't crash — the error banner
          // tells the user what happened.
          return { campaigns: [], leads: [] } as unknown as ReturnType<typeof api.getFeed> extends Promise<infer T> ? T : never;
        }
      },
    };
  }, [api, isReal]);

  // Reset error when switching back to mock.
  React.useEffect(() => {
    if (!isReal) setFeedError(null);
  }, [isReal]);

  const toolbarSlot = isSuperadmin ? (
    <div className="inline-flex items-center gap-2">
      <SalesDataSourceToggle />
      {!isReal && <MockDataSwitch />}
    </div>
  ) : undefined;

  return (
    <div className="mb-8">
      {/* Error banner for real-data failures */}
      {feedError && (
        <div className="mb-3 flex items-center gap-2.5 rounded-xl border border-red-500/25 bg-red-500/8 px-4 py-2.5 text-sm text-red-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <Icon icon="bx:error-circle" className="h-4 w-4 shrink-0 text-red-300" />
          <span className="flex-1">
            <span className="font-semibold">Real data error:</span>{' '}
            {feedError}
          </span>
          <span className="text-[10px] text-white/40">Switch to Mock to continue, or try again.</span>
        </div>
      )}

      {/* Loading overlay for the real data path */}
      {feedLoading && (
        <div className="mb-3 flex items-center gap-2.5 rounded-xl border border-cyan-300/15 bg-cyan-400/6 px-4 py-2.5 text-sm text-cyan-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <Icon icon="bx:loader-alt" className="h-4 w-4 animate-spin text-cyan-200" />
          <span>Loading real data from n8n-proxy…</span>
        </div>
      )}

      <UniBox
        config={salesConfig}
        api={wrappedApi}
        campaigns={campaigns}
        lens={lens}
        view={view}
        mailbox={mailbox}
        calRange={calRange}
        onLensChange={onLensChange}
        onViewChange={onViewChange}
        onMailboxChange={onMailboxChange}
        onCalRangeChange={onCalRangeChange}
        onUniBoxChange={onUniBoxChange}
        onOpenLead={onOpenLead ? (entity) => onOpenLead(entity.lvl1_id, String(entity.campaign_id || '')) : undefined}
        onOpenCampaignDetails={onOpenCampaignDetails}
        onSaveSalesState={onSaveSalesState}
        currency={teamPrefs?.currency}
        toolbarSlot={toolbarSlot}
      />
    </div>
  );
};

export default SalesUniBoxTab;
